import { useState } from 'react';
import { Alert, Button, Card, Checkbox, Modal, Progress, Space, Statistic, Switch, Typography, message as toast } from 'antd';
import PageTitle from 'components/page-title';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { supportError } from 'pages/support-shared/utils';
import { formatNumber } from 'pages/star-shared/utils';

const CLASSES = [
  { value: 'CREATOR', label: 'Creator' },
  { value: 'CREATOR_NPC', label: 'Creator NPC' },
  { value: 'STAFF', label: 'Nhân viên (Makeup, MC, Kỹ thuật, HR…)' },
  { value: 'AGENCY', label: 'Agency' },
];

interface BulkResult {
  eligible: Record<string, number>;
  total: number;
  processed: number;
  created: number;
  failed: number;
  remaining: number;
}

/**
 * Mở kênh hỗ trợ cho nhiều người một lần (tin chào + push). Chỉ ADMIN. Fan (User)
 * không có ở đây: mở hàng loạt cho fan = spam; fan được mở khi nhân viên nhắn
 * trước, hoặc khi app bản mới có nút "Liên hệ hỗ trợ".
 */
const BulkTab = () => {
  const [classes, setClasses] = useState<string[]>(['CREATOR', 'CREATOR_NPC', 'STAFF', 'AGENCY']);
  const [notify, setNotify] = useState(true);
  const [preview, setPreview] = useState<BulkResult | null>(null);
  const [counting, setCounting] = useState(false);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState({ created: 0, failed: 0, total: 0 });

  const count = async () => {
    setCounting(true);
    try {
      const res: any = await AxiosUtils.post(API.supportBulkOpen, { classes, dryRun: true });
      setPreview(res.data);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setCounting(false);
    }
  };

  const run = async () => {
    if (!preview?.total) return;
    setRunning(true);
    let created = 0;
    let failed = 0;
    const total = preview.total;
    setDone({ created, failed, total });
    try {
      // Mỗi lần gọi server xử lý tối đa 200 người; lặp tới khi hết hoặc không còn tiến triển.
      for (let round = 0; round < 500; round++) {
        const res: any = await AxiosUtils.post(API.supportBulkOpen, { classes, notify });
        const r: BulkResult = res.data;
        created += r.created;
        failed += r.failed;
        setDone({ created, failed, total });
        if (!r.remaining || r.processed === 0 || r.created === 0) break;
      }
      toast.success(`Đã mở kênh cho ${formatNumber(created)} người.`);
      setPreview(null);
    } catch (error) {
      toast.error(supportError(error));
    } finally {
      setRunning(false);
    }
  };

  const confirmRun = () =>
    Modal.confirm({
      title: `Mở kênh hỗ trợ cho ${formatNumber(preview?.total || 0)} người?`,
      content: notify
        ? 'Mỗi người nhận một tin chào từ “HP Media Hỗ trợ” và một thông báo đẩy. Không thu hồi hàng loạt được.'
        : 'Mỗi người nhận một tin chào (không gửi thông báo đẩy). Không thu hồi hàng loạt được.',
      okText: 'Mở kênh',
      cancelText: 'Huỷ',
      onOk: run,
    });

  const percent = done.total ? Math.min(100, Math.round(((done.created + done.failed) / done.total) * 100)) : 0;

  return (
    <div>
      <PageTitle title="Mở kênh hàng loạt" />
      <Card style={{ maxWidth: 820 }}>
        <Typography.Paragraph>
          Tạo sẵn cuộc trò chuyện với <strong>HP Media Hỗ trợ</strong> cho những người chưa có, kèm tin chào. Chỉ tài khoản đã
          xác thực, không bị khoá. Người đã có kênh được bỏ qua — chạy lại nhiều lần vẫn an toàn.
        </Typography.Paragraph>
        <Space direction="vertical" size={12} style={{ width: '100%' }}>
          <Checkbox.Group
            value={classes}
            onChange={(v) => {
              setClasses(v as string[]);
              setPreview(null);
            }}
            options={CLASSES}
          />
          <Space>
            <Switch checked={notify} onChange={setNotify} />
            <span>Gửi thông báo đẩy kèm tin chào</span>
          </Space>
          <Space wrap>
            <Button onClick={count} loading={counting} disabled={!classes.length || running}>
              Đếm trước
            </Button>
            <Button type="primary" onClick={confirmRun} disabled={!preview?.total || running} loading={running}>
              {preview ? `Mở kênh cho ${formatNumber(preview.total)} người` : 'Mở kênh'}
            </Button>
          </Space>
          {preview && (
            <Space size={32} wrap>
              {CLASSES.filter((c) => classes.includes(c.value)).map((c) => (
                <Statistic key={c.value} title={c.label} value={preview.eligible[c.value] || 0} />
              ))}
              <Statistic title="Tổng chưa có kênh" value={preview.total} />
            </Space>
          )}
          {(running || done.created > 0 || done.failed > 0) && (
            <div>
              <Progress percent={percent} status={running ? 'active' : 'normal'} />
              <Typography.Text type="secondary">
                Đã mở {formatNumber(done.created)}
                {done.failed ? ` · lỗi ${formatNumber(done.failed)}` : ''} / {formatNumber(done.total)}
              </Typography.Text>
            </div>
          )}
          <Alert
            type="info"
            showIcon
            message="Fan (User) không mở hàng loạt"
            description="Nhắn trước cho từng fan bằng nút “Nhắn mới” trong Hộp thư. Khi app có bản cập nhật với nút “Liên hệ hỗ trợ”, fan tự mở được."
          />
        </Space>
      </Card>
    </div>
  );
};

export default BulkTab;
